import { Input } from '@/components/atoms/Input';
import { Popover } from '@/components/atoms/Popover';
import { cn } from '@/lib/classNames';
import { SortBy } from '@/resources/assets/images';
import type { ChangeEvent } from 'react';
import { LIST_LIMIT } from '../../constants/ListLimit';
import type { SortByProps } from '../../page/ListsManagementPage';

type ListHeaderProps = {
	currentPage: number;
	totalElements: number;
	onFilterByName: (search: string) => void;
	sortByKey: SortByProps['key'] | null;
	onSortByChange: (key: SortByProps['key']) => void;
};

export const ListHeader = ({
	currentPage,
	totalElements,
	onFilterByName,
	sortByKey,
	onSortByChange
}: ListHeaderProps) => {
	const displayFirstIndex = (currentPage - 1) * LIST_LIMIT + 1;
	const isLastPageElementsListSmallerThenTotalElements =
		currentPage * LIST_LIMIT < totalElements;
	const displayLastIndex = isLastPageElementsListSmallerThenTotalElements
		? currentPage * LIST_LIMIT
		: totalElements;

	const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
		onFilterByName(event.target.value);
	};

	return (
		<header className="flex w-full flex-col justify-between gap-4 md:flex-row md:items-center md:justify-start lg:h-12">
			<Input
				type="search"
				placeholder="Search list name"
				rounded="md"
				padding="md"
				size="md"
				className="order-1 w-full md:max-w-80"
				onChange={handleChangeSearch}
			/>

			<span className="order-2 hidden h-full w-px bg-dm-gray md:block" />

			<p className="order-3 w-full whitespace-nowrap text-center text-dm-white md:order-2 md:w-fit md:text-start">
				Displaying {displayFirstIndex}-{displayLastIndex} of{' '}
				{totalElements} results
			</p>

			<Popover.Root>
				<Popover.Trigger
					className={cn(
						'group/sort-by',
						'order-2 w-full items-center justify-center gap-1.5 rounded-md border border-orange p-0 px-4 py-1 font-barlow text-sm font-semibold text-dm-white transition-colors',
						'md:order-3 md:ml-auto md:w-fit',
						'hover:bg-orange hover:text-dm-black',
						'focus:border-dm-white focus:bg-orange focus:text-dm-black'
					)}
				>
					<SortBy className="group-hover/sort-by:[&_path]:stroke-dm-black group-focus/sort-by:[&_path]:stroke-dm-black" />
					Sort by
				</Popover.Trigger>

				<Popover.Content
					className={cn(
						'flex flex-col overflow-hidden bg-dm-black4 p-0',
						'[&>:not(:last-child)]:rounded-none [&>:not(:last-child)]:border-b [&>:not(:last-child)]:border-dm-white'
					)}
					align="end"
				>
					<Popover.Button
						isSelected={sortByKey === 'name'}
						onClick={() => onSortByChange('name')}
					>
						Name
					</Popover.Button>
					<Popover.Button
						isSelected={sortByKey === 'businessIds'}
						onClick={() => onSortByChange('businessIds')}
					>
						Business
					</Popover.Button>
				</Popover.Content>
			</Popover.Root>
		</header>
	);
};
